import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
  SortingState,
} from '@tanstack/react-table';
import { createCustomerColumns, type Customer } from './CustomerTableColumns';
import {
  ChevronUpIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';

interface CustomerTableProps {
  customers: Customer[];
  loading?: boolean;
}

const CustomerTable: React.FC<CustomerTableProps> = ({
  customers,
  loading = false,
}) => {
  const [t] = useTranslation('customer');
  const navigate = useNavigate();
  const [sorting, setSorting] = useState<SortingState>([]);

  // Handle customer actions
  const handleCustomerAction = (customerId: string, action: string) => {
    switch (action) {
      case 'chat':
        // Navigate to chat with customer
        console.log('Start chat with customer:', customerId);
        // TODO: Implement navigation to chat
        break;
      case 'edit':
        // Navigate to edit customer
        console.log('Edit customer:', customerId);
        // TODO: Implement navigation to edit customer
        break;
      case 'history':
        // Navigate to customer history
        console.log('View customer history:', customerId);
        // TODO: Implement navigation to customer history
        break;
      case 'delete':
        // Show delete confirmation
        console.log('Delete customer:', customerId);
        // TODO: Implement delete customer functionality
        break;
      default:
        break;
    }
  };

  const columns = useMemo(
    () => createCustomerColumns(navigate, t, handleCustomerAction),
    [navigate, t],
  );

  const table = useReactTable({
    data: customers,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
    initialState: {
      pagination: {
        pageSize: 10,
      },
      sorting: [
        {
          id: 'name',
          desc: false,
        },
      ],
    },
  });

  if (loading) {
    return (
      <div className="w-full space-y-4">
        <div className="card bg-base-100 border-base-300 border shadow-xl">
          <div className="card-body">
            <div className="flex items-center justify-center py-12">
              <span className="loading loading-spinner loading-lg"></span>
              <span className="text-base-content ml-4">{t('loading', 'Loading...')}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (customers.length === 0) {
    return (
      <div className="w-full">
        <div className="card bg-base-100 border-base-300 border shadow-xl">
          <div className="card-body">
            <div className="text-center py-12">
              <p className="text-base-content/70">{t('no_customers', 'No customers found.')}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-4">
      {/* Results Summary */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">
          {t('search_results', 'Search Results')} ({customers.length})
        </h3>
        <div className="text-sm text-base-content/70">
          {t('showing_results', 'Showing {{start}}-{{end}} of {{total}} customers', {
            start: table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1,
            end: Math.min(
              (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
              customers.length
            ),
            total: customers.length,
          })}
        </div>
      </div>

      {/* Table */}
      <div className="card bg-base-100 border-base-300 border shadow-xl">
        <div className="card-body p-0">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr className="border-base-300">
                  {table.getFlatHeaders().map((header) => (
                    <th
                      key={header.id}
                      className="bg-base-200 text-base-content"
                    >
                      {header.isPlaceholder ? null : (
                        <div
                          className={`flex items-center gap-2 ${
                            header.column.getCanSort()
                              ? 'hover:text-base-content/80 cursor-pointer select-none'
                              : ''
                          }`}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                          {header.column.getCanSort() && (
                            <span className="flex flex-col">
                              {header.column.getIsSorted() === 'asc' ? (
                                <ChevronUpIcon className="h-3 w-3" />
                              ) : header.column.getIsSorted() === 'desc' ? (
                                <ChevronDownIcon className="h-3 w-3" />
                              ) : (
                                <div className="flex flex-col">
                                  <ChevronUpIcon className="h-3 w-3 text-base-content/30" />
                                  <ChevronDownIcon className="h-3 w-3 text-base-content/30 -mt-1" />
                                </div>
                              )}
                            </span>
                          )}
                        </div>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    className="border-base-300 hover:bg-base-200/50 transition-colors"
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="py-4">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Pagination Controls */}
      {table.getPageCount() > 1 && (
        <div className="flex items-center justify-between gap-2">
          <div className="join">
            <button
              className="join-item btn btn-sm"
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}
            >
              «
            </button>
            <button
              className="join-item btn btn-sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              ‹
            </button>
            <button
              className="join-item btn btn-sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              ›
            </button>
            <button
              className="join-item btn btn-sm"
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              disabled={!table.getCanNextPage()}
            >
              »
            </button>
          </div>

          <div className="text-sm">
            <span className="font-medium">
              {t('pagination.page', 'Page')} {table.getState().pagination.pageIndex + 1}{' '}
              {t('pagination.of', 'of')} {table.getPageCount()}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerTable;
